/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { map, isEmpty } from 'lodash';
import { Segment, List, Message, Loader } from 'semantic-ui-react';
import { Table, Color } from '../../../../components';
import { FormattedMessage } from 'react-intl';
import EditServiceGroupButton from './group/EditServiceGroupButton';
import AddServiceButton from './service/AddServiceButton';
import DeleteServiceButton from './service/DeleteServiceButton';
import EditServiceButton from './service/EditServiceButton';

import type {
    Service,
    ServiceGroup as ServiceGroupType,
} from '../../../../type';

// Query for list of service groups of current salon
export const GET_SERVICE_GROUPS_QUERY = gql`
    query getServiceGroups($salon: ID!, $search: String, $lang: String!) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    serviceGroups(search: $search, lang: $lang) {
                        pageInfo {
                            hasNextPage
                            endCursor
                        }
                        nodes {
                            id
                            ...serviceGroup
                        }
                    }
                }
            }
        }
    }

    fragment serviceGroup on ServiceGroup {
        name(lang: $lang)
        nameVariants {
            lv
            ru
            en
        }
        type
        services {
            id
            name(lang: $lang)
            nameVariants {
                lv
                ru
                en
            }
            duration
            price
            color
        }
    }
`;

type ServiceGroupProps = {
    serviceGroup: ServiceGroupType,
};

type ServiceItemProps = {
    serviceGroup: ServiceGroupType,
    service: Service,
};

const GroupName = styled.span`
    font-size: 16px;
    line-height: 30px;
    font-weight: bold;
`;

const CenteredMessage = styled(Message)`
    text-align: center;
`;

/**
 * Represents single service item
 */
const ServiceItem = ({ serviceGroup, service }: ServiceItemProps) => (
    <Table.Row>
        <Table.Cell>
            <Color size={16} spaced="right" color={service.color} />
            {service.name}
        </Table.Cell>
        <Table.Cell>
            {service.duration}{' '}
            <FormattedMessage
                id="pages.settings.services.servicesList.minutes"
                defaultMessage="minutes"
            />
        </Table.Cell>
        <Table.Cell textAlign="right">
            € {parseFloat(service.price).toFixed(2)}
        </Table.Cell>
        <Table.Cell textAlign="right">
            <List horizontal divided>
                <List.Item>
                    <EditServiceButton
                        serviceGroup={serviceGroup}
                        service={service}
                    />
                </List.Item>
                <List.Item>
                    <DeleteServiceButton
                        serviceGroup={serviceGroup}
                        service={service}
                    />
                </List.Item>
            </List>
        </Table.Cell>
    </Table.Row>
);

/**
 * Show this message if services list is empty
 */
const NoServicesMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={4} textAlign="center">
            <FormattedMessage
                id="pages.settings.services.servicesList.groupNoServices"
                defaultMessage="This group does not have any services"
            />
        </Table.Cell>
    </Table.Row>
);

/**
 * Represents group of services and it's service items
 */
const ServiceGroup = ({ serviceGroup }: ServiceGroupProps) => (
    <Segment.Group>
        <Segment attached="top" secondary clearing>
            <GroupName>{serviceGroup.name}</GroupName>
            <EditServiceGroupButton serviceGroup={serviceGroup} />
            <AddServiceButton serviceGroup={serviceGroup} />
        </Segment>
        <Segment attached="bottom">
            <Table fixed celled>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>
                            <FormattedMessage
                                id="pages.settings.services.servicesList.name"
                                defaultMessage="Name"
                            />
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <FormattedMessage
                                id="pages.settings.services.servicesList.duration"
                                defaultMessage="Duration"
                            />
                        </Table.HeaderCell>
                        <Table.HeaderCell textAlign="right">
                            <FormattedMessage
                                id="pages.settings.services.servicesList.price"
                                defaultMessage="Price"
                            />
                        </Table.HeaderCell>
                        <Table.HeaderCell textAlign="right">
                            <FormattedMessage
                                id="pages.settings.services.servicesList.actions"
                                defaultMessage="Actions"
                            />
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {isEmpty(serviceGroup.services) ? (
                        <NoServicesMessage />
                    ) : (
                        map(serviceGroup.services, service => (
                            <ServiceItem
                                key={service.id}
                                serviceGroup={serviceGroup}
                                service={service}
                            />
                        ))
                    )}
                </Table.Body>
            </Table>
        </Segment>
    </Segment.Group>
);

/**
 * Presents list of service groups for the current salon
 */
const ServicesList = () => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const searchQuery = useSelector(({ services }) =>
        services.get('searchQuery'),
    );
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );

    const { loading, data } = useQuery(GET_SERVICE_GROUPS_QUERY, {
        variables: {
            salon,
            search: searchQuery || undefined,
            lang: currentLanguage,
        },
        fetchPolicy: 'cache-and-network',
    });

    const serviceGroups =
        !loading && data && data.viewer
            ? data.viewer.salon.serviceGroups.nodes
            : [];

    // Show spinner while loading list of service groups
    if (loading && isEmpty(serviceGroups)) {
        return <Loader size="large" inline="centered" active />;
    }

    // Show this message if list of service groups is empty
    if (isEmpty(serviceGroups)) {
        return (
            <CenteredMessage>
                <FormattedMessage
                    id="pages.settings.services.servicesList.noServices"
                    defaultMessage="You don't have any services yet"
                />
            </CenteredMessage>
        );
    }

    return (
        <div>
            {map(serviceGroups, serviceGroup => (
                <ServiceGroup
                    key={serviceGroup.id}
                    serviceGroup={serviceGroup}
                />
            ))}
        </div>
    );
};

export default ServicesList;
